import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { identifyUser, clearUser } from './utils/analytics';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ListCases from './pages/cases/ListCases';
import CreateCase from './pages/cases/CreateCase';
import ViewCase from './pages/cases/ViewCase';
import Home from './pages/Home';
import { AuthProvider } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import CognitoCallback from './components/CognitoCallback';
import ProtectedRoute from './components/ProtectedRoute';
import ViewAnalysis from './pages/cases/ViewAnalysis';
import ViewFile from './pages/cases/ViewFile';
import ErrorBoundary from './components/ErrorBoundary';
import ViewStatementProcessing from './pages/cases/ViewStatementProcessing';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { AdminProvider } from './contexts/AdminContext';
import RequireAdmin from './components/admin/RequireAdmin';
import BlockAttorney from './components/attorney/BlockAttorney';
import RequireAttorney from './components/attorney/RequireAttorney';
import Organizations from './pages/admin/Organizations';
import Users from './pages/admin/Users';
import AttorneyCases from './pages/attorney/AttorneyCases';
import AttorneyPortal from './pages/attorney/AttorneyPortal';
import Benchmarks from './pages/admin/Benchmarks';
import Invoices from './pages/admin/Invoices';

// Dynamically determine redirect URI based on current domain.
// In dev, always use window.location.origin so the redirect works regardless of
// which port Vite happens to assign (5173, 5174, …).
// In production, same approach — the current origin is always correct.
/** Identifies the signed-in user in GA4 + Sentry; clears on sign-out. */
function UserAnalyticsTracker() {
  const auth = useOidcAuth();
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      const userId = auth.user.profile.sub ?? '';
      const email = (auth.user.profile.email as string) ?? '';
      const orgId = (auth.user.profile.website as string) ?? '';
      identifyUser(userId, email, orgId);
    } else if (!auth.isAuthenticated && !auth.isLoading) {
      clearUser();
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.user]);
  return null;
}

const getRedirectUri = () => `${window.location.origin}/cognito-callback`;

const cognitoAuthConfig = {
  authority: import.meta.env.VITE_COGNITO_AUTHORITY,
  client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
  redirect_uri: getRedirectUri(),
  response_type: "code",
  scope: "email openid phone profile",
  // Use localStorage instead of sessionStorage so auth persists across tabs
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Auto-refresh tokens before they expire
  automaticSilentRenew: true,
  // Handle the callback after login
  onSigninCallback: () => {
    // Remove the code and state from URL after successful login
    window.history.replaceState({}, document.title, window.location.pathname);
  }
};

function App() {
  return (
    <Router>
      <AuthProvider {...cognitoAuthConfig}>
        <UserAnalyticsTracker />
        <OrganizationProvider>
          <AdminProvider>
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Navbar />
              <main className="container mt-4 main-content">
                <Routes>
                <Route path="/" element={<Home />} />
                
                {/* Regular user routes - blocked for attorneys */}
                <Route path="/cases" element={
                  <ProtectedRoute>
                    <BlockAttorney>
                      <ListCases />
                    </BlockAttorney>
                  </ProtectedRoute>
                } />
                <Route path="/case/:case_id" element={
                  <ProtectedRoute>
                    <BlockAttorney>
                      <ViewCase />
                    </BlockAttorney>
                  </ProtectedRoute>
                } />
                <Route path="/case/:case_id/agent/:run_id" element={
                  <ProtectedRoute>
                    <BlockAttorney>
                      <ViewCase />
                    </BlockAttorney>
                  </ProtectedRoute>
                } />
                <Route path="/create-case" element={
                  <ProtectedRoute>
                    <BlockAttorney>
                      <CreateCase />
                    </BlockAttorney>
                  </ProtectedRoute>
                } />
                <Route path="/cases/:case_id/analyses/:analysis_id" element={
                  <ProtectedRoute>
                    <BlockAttorney>
                      <ViewAnalysis />
                    </BlockAttorney>
                  </ProtectedRoute>
                } />
                
                {/* Attorney-specific routes */}
                <Route path="/attorney" element={
                  <ProtectedRoute>
                    <RequireAttorney>
                      <AttorneyCases />
                    </RequireAttorney>
                  </ProtectedRoute>
                } />
                <Route path="/attorney/case/:case_id" element={
                  <ProtectedRoute>
                    <RequireAttorney>
                      <AttorneyPortal />
                    </RequireAttorney>
                  </ProtectedRoute>
                } />
                
                {/* Shared routes - accessible by both */}
                <Route path="/cognito-callback" element={<CognitoCallback />} />
                <Route path="/case/:case_id/file/:file_id" element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ViewFile />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route 
                  path="/case/:case_id/file/:file_id/processing" 
                  element={
                    <ProtectedRoute>
                      <BlockAttorney>
                        <ViewStatementProcessing />
                      </BlockAttorney>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Admin routes - blocked for attorneys */}
                <Route 
                  path="/admin/organizations" 
                  element={
                    <ProtectedRoute>
                      <RequireAdmin>
                        <Organizations />
                      </RequireAdmin>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/users" 
                  element={
                    <ProtectedRoute>
                      <RequireAdmin>
                        <Users />
                      </RequireAdmin>
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/admin/invoices"
                  element={
                    <ProtectedRoute>
                      <RequireAdmin>
                        <Invoices />
                      </RequireAdmin>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/benchmarks"
                  element={
                    <ProtectedRoute>
                      <Benchmarks />
                    </ProtectedRoute>
                  }
                />
              </Routes>
              </main>
              <Footer />
            </div>
          </AdminProvider>
        </OrganizationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;